<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ $t(successMessage) }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ $t(errorMessage) }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('departments.title') }}</h2>
    </div>

    <!-- Search Input -->
    <div class="mb-4">
      <input
          v-model="query"
          @input="searchDepartments"
          class="form-control"
          type="text"
          :placeholder="$t('departments.searchPlaceholder')"
      />
    </div>

    <!-- Add Department Button -->
    <div class="mb-4">
      <button v-if="hasPermission('users.add_department')" class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('add') }}
      </button>
    </div>

    <!-- Pagination Buttons -->
    <div class="pagination-controls">
      <button
          class="btn pagination-btn"
          @click="goToPreviousPage()"
          v-if="showPreviousButton"
      >
        <i class="bi bi-arrow-left"></i>
        {{ $t('previous') }}
      </button>
      <button
          class="btn pagination-btn"
          @click="goToNextPage()"
          v-if="showNextButton"
      >
        {{ $t('next') }}
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>

    <!-- Department List -->
    <div class="columns is-multiline">
      <div class="column is-12">
        <template v-if="departments.length">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>{{ $t('ID') }}</th>
              <th>{{ $t('name') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="department in departments" :key="department.id">
              <td>{{ department.id }}</td>
              <td>{{ department.name }}</td>
              <td>
                <!-- Edit button -->
                <button v-if="hasPermission('users.change_department')" class="btn btn-primary btn-sm"
                        @click="openModal('edit', department)">
                  <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
                </button>
                <!-- Delete button -->
                <button v-if="hasPermission('users.delete_department')" @click="openConfirmDelete(department.id)"
                        class="btn btn-danger btn-sm">
                  <i class="bi bi-trash"></i> {{ $t('delete') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- No departments available -->
        <div v-else>
          <p>{{ $t('departments.noDepartments') }}</p>
        </div>
      </div>
    </div>

    <!-- Bootstrap Modal for Add/Edit Department -->
    <div class="modal fade" id="departmentModal" tabindex="-1" aria-labelledby="departmentModalLabel"
         aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="departmentModalLabel">
              {{ isEditMode ? $t('departments.editTitle') : $t('departments.addTitle') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Add/Edit Form -->
            <form @submit.prevent="submitDepartment">
              <div class="mb-3">
                <label for="departmentName" class="form-label">{{ $t('departments.nameLabel') }}</label>
                <input v-model="departmentForm.name" type="text" class="form-control" id="departmentName" required/>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{
                $t('cancel')
              }}
            </button>
            <button type="button" class="btn btn-primary" @click="submitDepartment">
              {{ isEditMode ? $t('update') : $t('add') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('departments.confirmDeleteTitle') }}</h3>
        <p>{{ $t('departments.confirmDeleteMessage') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteDepartment">{{ $t('confirmDelete') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'DepartmentList',
  data() {
    return {
      departments: [],
      query: '', // Search query
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      successMessage: '',
      errorMessage: '',
      isEditMode: false,
      departmentForm: {
        id: null,
        name: ''
      },
      modalInstance: null,
      showConfirm: false,
      departmentIdToDelete: null
    };
  },
  computed: {
    store: () => useMainStore(),
    hasPermission() {
      return (permission) => {
        // Make sure user.permissions is available and is an array
        const userPermissions = this.store.user.permissions || [];
        return userPermissions.includes(permission);
      };
    }
  },

  mounted() {
    this.getDepartments();
    this.$nextTick(() => {
      const modalElement = document.getElementById('departmentModal');
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  methods: {
    openModal(mode, department = null) {
      if (mode === 'add') {
        this.isEditMode = false;
        this.departmentForm = {id: null, name: ''};
      } else if (mode === 'edit') {
        this.isEditMode = true;
        this.departmentForm = {...department};
      }
      this.modalInstance.show();
    },
    closeModal() {
      this.modalInstance.hide();
    },
    async submitDepartment() {
      try {
        if (this.isEditMode) {
          await axios.put(`/api/v1/admin/departments/${this.departmentForm.id}/`, this.departmentForm);
          this.successMessage = 'Department updated successfully';
        } else {
          await axios.post(`/api/v1/admin/departments/`, this.departmentForm);
          this.successMessage = 'Department added successfully';
        }
        this.getDepartments();
      } catch (error) {
        this.errorMessage = 'Failed to save department';
      } finally {
        this.closeModal();
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    openConfirmDelete(departmentId) {
      this.departmentIdToDelete = departmentId;
      this.showConfirm = true;
    },
    async deleteDepartment() {
      try {
        await axios.delete(`/api/v1/admin/departments/${this.departmentIdToDelete}/`);
        this.successMessage = 'Department deleted successfully';
        this.getDepartments();
      } catch (error) {
        this.errorMessage = 'Failed to delete department';
      } finally {
        this.showConfirm = false;
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    cancelDelete() {
      this.showConfirm = false;
    },
    async getDepartments() {
      try {
        const response = await axios.get(`/api/v1/admin/departments/?page=${this.currentPage}&search=${this.query}`);
        this.departments = response.data.results;
        this.showNextButton = Boolean(response.data.next);
        this.showPreviousButton = Boolean(response.data.previous);
      } catch (error) {
        this.errorMessage = 'Failed to fetch departments';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
      }
    },
    searchDepartments() {
      this.currentPage = 1;
      this.getDepartments();
    },
    goToNextPage() {
      this.currentPage += 1;
      this.getDepartments();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getDepartments();
    }
  }
};
</script>
